import React, { useState } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Badge,
  useTheme,
  useMediaQuery,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon
} from '@mui/material';
import {
  Menu as MenuIcon,
  School as SchoolIcon,
  Quiz as QuizIcon,
  EmojiEvents as TrophyIcon,
  Translate as TranslateIcon,
  Star as StarIcon,
  Home as HomeIcon,
  Search as SearchIcon,
  Settings as SettingsIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';

interface NavigationMenuProps {
  token: string | null;
  onNavigate: (section: string) => void;
  activeSection: string;
  userStats?: {
    lessons_completed: number;
    days_streak: number;
    terms_mastered: number;
    letters_learned: number;
    time_spent: number;
  };
}

const NavigationMenu: React.FC<NavigationMenuProps> = ({
  token,
  onNavigate,
  activeSection,
  userStats
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const navigationItems = [
    {
      id: 'home',
      label: 'Главная',
      icon: <HomeIcon />,
      description: 'Обзор всех возможностей'
    },
    {
      id: 'lessons',
      label: 'Уроки',
      icon: <SchoolIcon />,
      description: 'Изучение арабских букв',
      badge: userStats?.lessons_completed || 0
    },
    {
      id: 'quiz',
      label: 'Квизы',
      icon: <QuizIcon />,
      description: 'Проверка знаний',

    },
    {
      id: 'achievements',
      label: 'Достижения',
      icon: <TrophyIcon />,
      description: 'Ваши успехи',

    },
    {
      id: 'leaderboard',
      label: 'Прогресс',
      icon: <TrendingUpIcon />,
      description: 'Сохранённый прогресс уроков'
    },
    {
      id: 'allah-names',
      label: 'Имена Аллаха',
      icon: <StarIcon />,
      description: '99 прекрасных имен'
    },
    {
      id: 'terms',
      label: 'Термины',
      icon: <TranslateIcon />,
      description: 'Исламская терминология'
    },
    {id:'search',label:'Поиск терминов',icon:<SearchIcon/>,description:'Сохранённый справочник'},
    {id:'profile',label:'Профиль',icon:<PersonIcon/>,description:'Личные данные'},
    {id:'settings',label:'Настройки',icon:<SettingsIcon/>,description:'Настройки приложения'}
  ];

  const quickActions = [
    {
      icon: <SearchIcon />,
      name: 'Поиск',
      action: () => onNavigate('search')
    },
    {
      icon: <SettingsIcon />,
      name: 'Настройки',
      action: () => onNavigate('settings')
    },
    {
      icon: <PersonIcon />,
      name: 'Профиль',
      action: () => onNavigate('profile')
    }
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSpeedDialToggle = () => {
    setSpeedDialOpen(!speedDialOpen);
  };

  const drawer = (
    <Box>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Арабские уроки
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {navigationItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton
              selected={activeSection === item.id}
              onClick={() => {
                onNavigate(item.id);
                setMobileOpen(false);
              }}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                },
              }}
            >
              <ListItemIcon sx={{ color: activeSection === item.id ? 'inherit' : undefined }}>
                {item.badge ? (
                  <Badge badgeContent={item.badge} color="secondary">
                    {item.icon}
                  </Badge>
                ) : (
                  item.icon
                )}
              </ListItemIcon>
              <ListItemText 
                primary={item.label}
                secondary={item.description}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  if (isMobile) {
    return (
      <>
        <AppBar position="static" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div">
              {navigationItems.find(item => item.id === activeSection)?.label || 'Арабские уроки'}
            </Typography>
          </Toolbar>
        </AppBar>

        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280 },
          }}
        >
          {drawer}
        </Drawer>

        {/* Speed Dial для быстрых действий */}
        <SpeedDial
          ariaLabel="Quick actions"
          sx={{ position: 'fixed', bottom: 16, right: 16 }}
          icon={<SpeedDialIcon />}
          onClose={handleSpeedDialToggle}
          onOpen={handleSpeedDialToggle}
          open={speedDialOpen}
        >
          {quickActions.map((action) => (
            <SpeedDialAction
              key={action.name}
              icon={action.icon}
              tooltipTitle={action.name}
              onClick={action.action}
            />
          ))}
        </SpeedDial>
      </>
    );
  }

  return (
    <AppBar position="static" sx={{ mb: 3 }}>
      <Toolbar sx={{flexWrap:'wrap',gap:1,py:1}}>
        <SchoolIcon sx={{ mr: 2 }} />
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          Арабские уроки
        </Typography>
        
        {/* Десктопная навигация */}
        <Box sx={{ display: 'flex', flexWrap:'wrap', gap: 1, alignItems: 'center' }}>
          {navigationItems.map((item) => (
            <Chip
              key={item.id}
              icon={item.badge ? (
                <Badge badgeContent={item.badge} color="secondary">
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )}
              label={item.label}
              onClick={() => onNavigate(item.id)}
              color={activeSection === item.id ? 'primary' : 'default'}
              variant={activeSection === item.id ? 'filled' : 'outlined'}
              sx={{ 
                cursor: 'pointer',color:'white',borderColor:'rgba(255,255,255,.65)','& .MuiChip-icon':{color:'inherit'},
                '&:hover': {
                  backgroundColor: theme.palette.action.hover,
                }
              }}
            />
          ))}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default NavigationMenu;
